import { Stack, Button, Dialog, Typography, DialogContent, DialogTitle, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import * as React from 'react';
import TargetEditor from './TargetEditor';
import * as Utils from '../util/Utils';
import {ProjectContext} from "../schedule/ProjectContext";
import * as ATCAConstants from '../util/ATCAConstants'

export default function NewScanDialog(
  props: {
    addTarget: (scan: any, target: any) => void,
    message: string,
    open: boolean,
    handleClose: () => void
}) {
  const projectContext = React.useContext(ProjectContext);
  const [scanSetting, setScanSetting] = React.useState<any>(ATCAConstants.DEFAULT_SETTING);

  const [target, setTarget] = React.useState<any>(ATCAConstants.INIT_TARGET);
  const [isScanValid, setIsScanValid] = React.useState(false);

  const addScan = () => {
    if (!target['name']) {
      if ((target['coord_sys']||'J2000') === 'J2000') {
        const ra = target['ra']||'';
        const dec = target['dec']||'';
        // generate a name
        let name = 'J';
        const ras = ra.split(':');
        const decs = dec.split(':');

        if ((ras.length>2) && (decs.length>2)) {
          name += ras[0] + ras[1];
          if (decs[0].startsWith('-') || decs[0].startsWith('+')) {
            name += decs[0];
          } else {
            name += '+' + decs[0];
          }

          name += decs[1];
          target['name'] = name;
        }
      }
    }
    if (target['isCalibrator']) {
      scanSetting['isCalibrator'] = true;
    }
    props.addTarget(scanSetting, target);
  }

  const setScanValue = (fieldName: string, value: any) => {
    setScanSetting({
      ...scanSetting,
      [fieldName]: value
    });
  }

  React.useEffect(() => {
    if (!props.open) {
      setTarget(ATCAConstants.INIT_TARGET);
    }
  }, [props.open]);

  React.useEffect(() => {
    const newIsValid = Utils.validateTarget(target);
    if (!newIsValid['isValid']) {
      setIsScanValid(false);
    }

    for (let key of Object.keys(scanSetting)) {
      if (!scanSetting[key]) {
        setIsScanValid(false);
        return;
      }
    }
    setIsScanValid(true);
  }, [target, scanSetting]);

  function renderCorrelatorConfig(config: any): React.ReactNode {
    const setting = projectContext.getCorrelatorSetting(
      config['correlator_setting'], config['frequency_configuration']['receiver']);
    const freqConfig = setting['frequency_configuration'] || {};

    return (<Stack direction="row" spacing={2}>
      <Typography variant="body2">
        {freqConfig['receiver']}
      </Typography>
      <Typography variant="body2">
        {(setting['spectral_points_per_subband']
          * setting['subbands_per_IF']
          * setting['number_of_IF'])} points
      </Typography>

      <Typography variant="body2">
        {(config['sub_band_configuration'] || []).length} zooms
      </Typography>

      <Typography variant="body2">
        {projectContext.calculateDataRate(
          config['name'], freqConfig['receiver'])} kB/s
      </Typography>
    </Stack>);
  }

  return (
    <Dialog open={props.open} maxWidth="sm" fullWidth
      aria-labelledby="new-target-dialog-title"
      onClose={props.handleClose}>
        
      <DialogTitle id="new-target-dialog-title">
        Add New Scan
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <TargetEditor showCalibrator={true} target={target} setTarget={setTarget}/>

          <Stack direction={'row'} spacing={2}>

              <TextField id="duration" label="Duration (seconds)"
              value={scanSetting['duration'] || 0}
              error={!scanSetting['duration']}
              onChange={(e) => setScanValue('duration', Number(e.target.value))}
              variant="standard" sx={{ flexGrow: 2 }}/>

              <FormControl variant="standard" sx={{ minWidth: '125px' }}>
                <InputLabel id="scan-type-label">Scan Type</InputLabel>
                <Select
                  labelId="scan-type-label"
                  id="scan-type"
                  label="Scan Type"
                  displayEmpty={true}
                  onChange={e => setScanValue('scanType', e.target.value as string)}
                  value={scanSetting['scanType'] || ''}
                >
                  {
                    ATCAConstants.SCAN_TYPES.map((type) => (
                      <MenuItem key={type} value={type} disabled={ATCAConstants.DISABLE_SCAN_TYPES.includes(type)}>
                        {type}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <FormControl variant="standard" sx={{ minWidth: '125px' }}>
                <InputLabel id="pointing-label">Pointing</InputLabel>
                <Select
                  labelId="pointing-label"
                  id="pointing"
                  label="Pointing"
                  displayEmpty={true}
                  onChange={e => setScanValue('pointing', e.target.value as string)}
                  value={scanSetting['pointing'] || ''}
                >
                  {
                    ATCAConstants.POINTINGS.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
          </Stack>

          <FormControl error={!scanSetting['correlator_configuration']}>
            <InputLabel id="corr-config-label">Correlator Configuration</InputLabel>
            <Select
              labelId="corr-config-label"
              id="corr-config"
              label="Correlator Configuration"
              displayEmpty={true}
              renderValue={(selected) => selected}
              onChange={e => setScanValue('correlator_configuration', e.target.value as string)}
              value={scanSetting['correlator_configuration'] || ''}
            >
              {
                projectContext.correlatorConfigurations.map((config, index) => (
                  <MenuItem key={config['name'] + '-' + index} value={config['name']}>
                    <ListItemText primary={config['name']}
                      secondary={renderCorrelatorConfig(config)}
                    />
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>                            

          <Button 
            disabled={!isScanValid}
            variant="outlined" onClick={addScan}>
            Add
          </Button>
          <Typography variant="caption" >
            {props.message}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}